import React from "react";
import { Link } from "react-router-dom";

const ENGLISH_DONGLE_URL = "https://autoshinycountershop.com/products/dongle";
const GERMAN_DONGLE_URL = "https://autoshinycountershop.com/de/products/dongle";

function isVisitorFromGermany() {
  try {
    if (Intl.DateTimeFormat().resolvedOptions().timeZone === "Europe/Berlin") {
      return true;
    }
  } catch {
    // Timezone lookup can fail in locked-down browsers.
  }

  const primaryLanguage = (navigator.language || "")
    .toLowerCase()
    .replace("_", "-");

  return primaryLanguage === "de" || primaryLanguage.startsWith("de-de");
}

export default function App() {
  const dongleShopUrl = isVisitorFromGermany()
    ? GERMAN_DONGLE_URL
    : ENGLISH_DONGLE_URL;
  return (
    <div className="app-container">
      <div className="background-pattern"></div>

      <div className="main-content">
        {/* Header with App Store Links */}
        <div className="header">
          <img
            src="/logo.png"
            alt="Auto Shiny Counter Logo"
            className="app-logo"
          />
          <h1 className="title">Auto Shiny Counter</h1>
          <p className="subtitle">
            Automatic shiny hunt tracking & switch multi automated hunting &
            multi-3DS remote control app
          </p>
          <p className="description">
            Automatically track and run your shiny hunts – perfect for
            dual-console hunters!
          </p>

          <div className="store-links">
            <a
              href="https://apps.apple.com/de/app/auto-shiny-counter/id6749230127"
              target="_blank"
              rel="noopener noreferrer"
              className="store-link"
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on the App Store"
                className="store-badge"
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.hellenbrand.autoShinyCounter"
              target="_blank"
              rel="noopener noreferrer"
              className="store-link"
            >
              <img
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="store-badge play-store-badge"
              />
            </a>
          </div>

          <a
            href={dongleShopUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shop-button"
          >
            🛒 Buy Dongle in the Shop
          </a>
        </div>

        {/* App Features */}
        <div className="features-section">
          <h2 className="features-title">✨ Features</h2>
          <ul className="features-list">
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Automatically counts resets using your phone camera
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Supports dual-console hunts (e.g. two Switches at once)
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Retro-inspired design with shiny sparkle aesthetics
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Manual and automatic counting modes
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Accurate tracking for full odds and Masuda Method hunts
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Fully offline – no account required
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Pro Feature: Remote control for modded 3DS – control your console
              with any controller connected to your phone (including Joy-Cons),
              with custom button, macro and sequence mappings for automated
              shiny hunts and automatic counter updates on button press.
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              With the Auto Shiny Counter Dongle, you can count resets
              automatically while hunting by playing normally with your default
              controller.
            </li>
            <li className="feature-item">
              <span className="feature-check">✨</span>
              Only with the Auto Shiny Counter Dongle: Use one controller for
              one or multiple Nintendo Switch consoles, including macro and
              button mappings for fast shiny hunting automation.
            </li>
          </ul>
        </div>

        {/* Full Description */}
        <div className="description-section">
          <h2 className="description-title">About Auto Shiny Counter</h2>
          <p className="full-description">
            Are you tired of manually counting resets while shiny hunting?
            Whether you're playing on handheld systems or home consoles – Auto
            Shiny Counter makes shiny hunting easier and more efficient than
            ever.
          </p>
          <p className="full-description">
            Perfect for serious shiny hunters who want to stay focused without
            losing track. Just aim your phone at your screen(s), and let the app
            do the counting!
          </p>
          <p className="full-description highlight">
            🎯 No more missed counts. No more distractions. Just clean shiny
            tracking.
          </p>
        </div>

        {/* SEO-Friendly FAQ / AI-readable Explanation */}
        <div className="description-section">
          <h2 className="description-title">What is Auto Shiny Counter?</h2>
          <p className="full-description">
            Auto Shiny Counter is a mobile app for iOS and Android that is built
            specifically for shiny hunters in monster-collecting games. The app
            automatically tracks your shiny encounters for wild hunts, soft
            resets and eggs, so you no longer have to manually tap a button or
            keep notes while hunting.
          </p>
          <p className="full-description">
            The app can be used with Nintendo Switch, Nintendo 3DS and other
            consoles by simply pointing your phone camera at the screens. It
            detects the resets and keeps an accurate counter for long term shiny
            hunts, including full odds and Masuda Method hunts.
          </p>
          <h2 className="description-title">
            Can I control a 3DS with a Switch Pro Controller?
          </h2>
          <p className="full-description">
            Yes. With the Auto Shiny Counter app and a modded Nintendo 3DS, you
            can remotely control one or multiple 3DS systems using any
            controller that is connected to your phone, including a Switch Pro
            Controller, Joy-Cons, Xbox controllers or PlayStation controllers.
            The app offers 3DS remote control, custom button mapping, macro and
            sequence mapping so you can automate your shiny hunting routine and
            even link specific button presses directly to your shiny counter.
          </p>
          <h2 className="description-title">
            Can I control multiple 3DS systems with my phone?
          </h2>
          <p className="full-description">
            Yes. Auto Shiny Counter is designed to control from one up to many
            modded 3DS consoles at the same time. You can send inputs to
            multiple 3DS systems either from your phone directly or via a
            controller connected to your phone, while the app keeps tracking
            your shiny encounters.
          </p>
          <h2 className="description-title">
            What is the Auto Shiny Counter Dongle for Switch?
          </h2>
          <p className="full-description">
            The Auto Shiny Counter Dongle enables Switch mode setup for
            automatic reset counting while shiny hunting. You keep playing
            normally with your default controller, and the dongle sends the
            input data needed for reliable shiny reset tracking.
          </p>
          <p className="full-description">
            Only with the Auto Shiny Counter Dongle, you can use one controller
            with one or multiple Nintendo Switch consoles, configure custom
            button mappings, and run macros for faster and more consistent shiny
            hunt automation.
          </p>
          <p className="full-description">
            If you want to buy the dongle hardware for Auto Shiny Counter, use
            the shop button on this page to open autoshinycountershop.com.
          </p>
        </div>

        {/* About Section */}
        <div className="about-section">
          <img
            src="/ab2f96b8-66ed-44c5-ae89-9da0364657f1.png"
            alt="Sebastian Wagner"
            className="profile-picture"
          />
          <div className="about-content">
            <h3>About the Developer</h3>
            <p>
              Hi, I'm Sebastian Wagner from Germany. I'm a long-time shiny
              hunting enthusiast and developer who created this app to help the
              shiny hunting community. If you find it useful, consider
              supporting the project!
            </p>
          </div>
        </div>

        {/* PayPal Donation Section */}
        <div className="donation-section">
          <div className="donation-buttons">
            <a
              href="https://www.paypal.com/donate/?hosted_button_id=3DJRXPPWGL5C6"
              target="_blank"
              rel="noopener noreferrer"
              className="paypal-button"
            >
              💳 Support with PayPal
            </a>
            <a
              href="https://ko-fi.com/sebastianhellenbrand"
              target="_blank"
              rel="noopener noreferrer"
              className="kofi-button"
            >
              ☕ Support on Ko-fi
            </a>
            <button
              className="bitcoin-button"
              onClick={(e) => {
                const address = "3Gny5pdgjSGKvn3ChpA3XWEvMMv44EDfeE";
                navigator.clipboard
                  .writeText(address)
                  .then(() => {
                    const button = e.target;
                    const originalText = button.textContent;
                    button.textContent = "₿ Address Copied!";
                    setTimeout(() => {
                      button.textContent = originalText;
                    }, 2000);
                  })
                  .catch(() => {
                    console.log("Could not copy Bitcoin address to clipboard");
                  });
              }}
            >
              ₿ Support with Bitcoin
            </button>
          </div>
          <p className="support-text">Thanks for your support! ❤️</p>
        </div>

        {/* Feedback Section */}
        <div className="feedback-section">
          <a
            href="mailto:sebastian-wagner-96@gmx.de?subject=Auto Shiny Counter - Feedback"
            className="feedback-button"
            onClick={(e) => {
              // Copy email to clipboard
              navigator.clipboard
                .writeText("sebastian-wagner-96@gmx.de")
                .then(() => {
                  // Show a brief notification that email was copied
                  const button = e.target;
                  const originalText = button.textContent;
                  button.textContent = "📧 Email Copied!";
                  setTimeout(() => {
                    button.textContent = originalText;
                  }, 2000);
                })
                .catch(() => {
                  // Fallback if clipboard API fails
                  console.log("Could not copy email to clipboard");
                });
            }}
          >
            📧 Send Feedback
          </a>
          <p className="feedback-text">
            Have suggestions or found a bug? Let me know!
          </p>
        </div>

        {/* Footer with Legal Links */}
        <div className="footer">
          <div className="footer-links">
            <Link to="/privacy-policy" className="footer-link">
              Website Privacy Policy
            </Link>
            <span className="footer-separator">•</span>
            <Link to="/app-privacy-policy" className="footer-link">
              App Privacy Policy
            </Link>
            <span className="footer-separator">•</span>
            <Link to="/app-terms-of-use" className="footer-link">
              App Terms of Use
            </Link>
            <span className="footer-separator">•</span>
            <Link to="/terms-and-conditions" className="footer-link">
              Terms & Conditions
            </Link>
            <span className="footer-separator">•</span>
            <Link to="/support/compliance" className="footer-link">
              EU Declaration of Conformity
            </Link>
            <span className="footer-separator">•</span>
            <Link to="/support/userguide" className="footer-link">
              User Guide PDFs
            </Link>
          </div>
          <p className="footer-copyright">
            © 2024 Sebastian Wagner. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
